import React from 'react'
import { Box, Card, Typography, List, ListItemButton, ListItemText, Stack } from '@mui/material'
import { Receipt, Description, CreditCard } from '@mui/icons-material'
import { ISingleDocument } from '@typings'
import { useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import 'dayjs/locale/nl'
import { StatusBadge } from '../status-badge'

interface RelatedDocumentsProps {
  document: ISingleDocument
}

export const RelatedDocuments: React.FC<RelatedDocumentsProps> = ({ document }) => {
  const navigate = useNavigate()

  // Collect all related documents to display
  const relatedDocs: JSX.Element[] = []

  // For credit notes - show the original invoice
  if (document.type === 'invoice' && document.is_credit_note && document.credited_invoice) {
    relatedDocs.push(
      <Card key="credited-invoice" sx={{ mb: 2 }} elevation={1}>
        <Box sx={{ p: 1.5, pb: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Creditnota voor
          </Typography>
        </Box>
        <List dense disablePadding>
          <ListItemButton
            onClick={() => navigate(`/facturen/${document.credited_invoice?.id}`)}
            sx={{ py: 1 }}>
            <Receipt sx={{ mr: 1.5, fontSize: 20, color: 'text.secondary' }} />
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    Factuur {document.credited_invoice.document_number || 'Concept'}
                  </Typography>
                  {document.credited_invoice.payment_status && (
                    <StatusBadge
                      status={document.credited_invoice.payment_status}
                      type="payment"
                      size="small"
                    />
                  )}
                </Box>
              }
              secondary={
                document.credited_invoice.issued_at
                  ? dayjs(document.credited_invoice.issued_at).locale('nl').format('DD MMM YYYY')
                  : null
              }
              primaryTypographyProps={{ fontSize: '0.875rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItemButton>
        </List>
      </Card>
    )
  }

  // For regular invoices - show source quotation if exists
  if (document.type === 'invoice' && !document.is_credit_note && document.quotation) {
    relatedDocs.push(
      <Card key="source-quotation" sx={{ mb: 2 }} elevation={1}>
        <Box sx={{ p: 1.5, pb: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Bron offerte
          </Typography>
        </Box>
        <List dense disablePadding>
          <ListItemButton
            onClick={() => navigate(`/offertes/${document.quotation?.id}`)}
            sx={{ py: 1 }}>
            <Description sx={{ mr: 1.5, fontSize: 20, color: 'text.secondary' }} />
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    Offerte {document.quotation.document_number}
                  </Typography>
                  {document.quotation.quotation_status && (
                    <StatusBadge
                      status={document.quotation.quotation_status}
                      type="quotation"
                      size="small"
                    />
                  )}
                </Box>
              }
              secondary={
                document.quotation.issued_at
                  ? dayjs(document.quotation.issued_at).locale('nl').format('DD MMM YYYY')
                  : null
              }
              primaryTypographyProps={{ fontSize: '0.875rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItemButton>
        </List>
      </Card>
    )
  }

  // For invoices with credit notes - show the credit notes
  if (
    document.type === 'invoice' &&
    !document.is_credit_note &&
    document.credit_notes &&
    document.credit_notes.length > 0
  ) {
    relatedDocs.push(
      <Card key="credit-notes" sx={{ mb: 2 }} elevation={1}>
        <Box sx={{ p: 1.5, pb: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Creditnota&apos;s
          </Typography>
        </Box>
        <List dense disablePadding>
          {document.credit_notes.map((creditNote) => (
            <ListItemButton
              key={creditNote.id}
              onClick={() => navigate(`/facturen/${creditNote.id}`)}
              sx={{ py: 1 }}>
              <CreditCard sx={{ mr: 1.5, fontSize: 20, color: 'warning.main' }} />
              <ListItemText
                primary={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">
                      Creditnota {creditNote.document_number || 'Concept'}
                    </Typography>
                    {creditNote.payment_status && (
                      <StatusBadge
                        status={creditNote.payment_status}
                        type="payment"
                        size="small"
                      />
                    )}
                  </Box>
                }
                secondary={
                  creditNote.issued_at
                    ? dayjs(creditNote.issued_at).locale('nl').format('DD MMM YYYY')
                    : null
                }
                primaryTypographyProps={{ fontSize: '0.875rem' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
            </ListItemButton>
          ))}
        </List>
      </Card>
    )
  }

  // For quotations - show generated invoice
  if (document.type === 'quotation' && document.generated_invoice) {
    relatedDocs.push(
      <Card key="generated-invoice" sx={{ mb: 2 }} elevation={1}>
        <Box sx={{ p: 1.5, pb: 0 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Gegenereerde factuur
          </Typography>
        </Box>
        <List dense disablePadding>
          <ListItemButton
            onClick={() => navigate(`/facturen/${document.generated_invoice?.id}`)}
            sx={{ py: 1 }}>
            <Receipt sx={{ mr: 1.5, fontSize: 20, color: 'text.secondary' }} />
            <ListItemText
              primary={
                <Box display="flex" alignItems="center" gap={1}>
                  <Typography variant="body2">
                    Factuur {document.generated_invoice.document_number || 'Concept'}
                  </Typography>
                  {document.generated_invoice.payment_status && (
                    <StatusBadge
                      status={document.generated_invoice.payment_status}
                      type="payment"
                      size="small"
                    />
                  )}
                </Box>
              }
              secondary={
                document.generated_invoice.issued_at
                  ? dayjs(document.generated_invoice.issued_at).locale('nl').format('DD MMM YYYY')
                  : null
              }
              primaryTypographyProps={{ fontSize: '0.875rem' }}
              secondaryTypographyProps={{ fontSize: '0.75rem' }}
            />
          </ListItemButton>
        </List>
      </Card>
    )
  }

  // Return all related documents or null if none
  if (relatedDocs.length === 0) {
    return null
  }

  return <Stack spacing={0}>{relatedDocs}</Stack>
}
